// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- uuid is used as a fallback for environments where crypto.randomUUID is unavailable
import { v4 as uuid } from 'uuid';

import { Side } from '@atlaskit/editor-common/selection';
import { GapCursorSelection } from '@atlaskit/editor-common/selection/gap-cursor/selection';
import type { EditorCommand } from '@atlaskit/editor-common/types/editor-command';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import { NodeSelection, TextSelection } from '@atlaskit/editor-prosemirror/state';
import { CellSelection } from '@atlaskit/editor-tables/cell-selection';
import { UNSAFE_expValNoExposure } from '@atlaskit/platform-feature-experiments/unsafe-exp-val-no-exposure';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';
import type { RemixHistoryEntryForUpdate } from '@atlassian/malleable-ui-components/types';

import type { MalleableUiPlugin } from '../malleableUiPluginType';
import { malleableUiPluginKey } from '../pm-plugins/plugin-key';
import type {
	RemixThinkingPluginMeta,
	RemixThinkingPresentationUpdate,
} from '../pm-plugins/remix-thinking/remix-thinking-plugin';
import { remixThinkingPluginKey } from '../pm-plugins/remix-thinking/remix-thinking-plugin';
import type { RemixModalMetadata } from '../types';

const randomId = (): string => {
	if (globalThis?.crypto?.randomUUID) {
		return globalThis.crypto.randomUUID();
	}
	// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- uuid is used as a fallback for environments where crypto.randomUUID is unavailable
	return uuid();
};

export const makeStartRemixThinking =
	(api: ExtractInjectionAPI<MalleableUiPlugin> | undefined) =>
	({
		contentType,
		dynamicUiSubtypeForUpdate,
		isUpdate,
		historyEntryForUpdate,
		remixAttemptId,
		remixModalMetadata,
		suppressThinkingHighlight,
	}: {
		contentType?: string;
		dynamicUiSubtypeForUpdate?: string;
		historyEntryForUpdate?: RemixHistoryEntryForUpdate;
		isUpdate?: boolean;
		remixAttemptId?: string;
		remixModalMetadata?: RemixModalMetadata;
		suppressThinkingHighlight?: boolean;
	} = {}): EditorCommand =>
	({ tr }) => {
		const selection = tr.selection;
		const isNodeSelection = selection instanceof NodeSelection;
		const isCellSelection = selection instanceof CellSelection;

		const presentation: Extract<RemixThinkingPresentationUpdate, { kind: 'start' }> = {
			kind: 'start',
			contentType,
			remixAttemptId: remixAttemptId ?? randomId(),
			isUpdate,
			historyEntryForUpdate,
			dynamicUiSubtypeForUpdate,
			thinkingSelectionFrom: selection.from,
			thinkingSelectionTo: isNodeSelection
				? selection.from + selection.node.nodeSize
				: selection.to,
			isNodeSelection: isNodeSelection,
			suppressThinkingHighlight,
		};

		if (isCellSelection) {
			presentation.cellSelectionRanges = selection.ranges.map((range) => ({
				from: range.$from.pos,
				to: range.$to.pos,
			}));
			presentation.anchorCellPos = selection.$anchorCell.pos;
			presentation.headCellPos = selection.$headCell.pos;
		}

		const thinkingMeta: RemixThinkingPluginMeta = {
			presentation,
			lifecycle: { kind: 'legacy-start' },
		};

		// Clear block selection decorations before applying the thinking state.
		api?.selection?.commands?.clearBlockSelection()({ tr });

		tr.setMeta(remixThinkingPluginKey, thinkingMeta);

		/**
		 * Added this check so that when a userPrompt is pre-supplied in metadata (e.g. from the AI Image Generation flow),
		 * we can pass in necessary metadata to the malleable-ui plugin.
		 * like the userPrompt
		 */
		const isRovoChatRemix =
			remixModalMetadata?.remixSource === 'rovoChat' &&
			remixModalMetadata.preselectedType !== undefined &&
			UNSAFE_expValNoExposure('cc-ce-maui-remix-menu-multivariant', 'cohort', 'control') ===
				'variant2';

		if (
			remixModalMetadata &&
			(isRovoChatRemix ||
				(remixModalMetadata.userPrompt &&
					((remixModalMetadata.remixSource === 'suggestedRemix' &&
						UNSAFE_expValNoExposure(
							'platform_editor_ai_content_suggested_edits',
							'isEnabled',
							false,
						)) ||
						expValEqualsNoExposure(
							'cc_page_experiences_editor_image_generation',
							'isEnabled',
							true,
						))))
		) {
			tr.setMeta(malleableUiPluginKey, {
				isRemixModalOpen: false,
				remixModalMetadata,
				remixSource: remixModalMetadata.remixSource,
				remixUpdateScrollTargetUrl: undefined,
			});
		} else {
			tr.setMeta(malleableUiPluginKey, {
				isRemixModalOpen: false,
				remixUpdateScrollTargetUrl: undefined,
			});
		}

		// Move the active selection away from the selected node so the user can
		// continue editing while the remix is processing.
		const endPos = isNodeSelection ? selection.from + selection.node.nodeSize : selection.to;

		if (isNodeSelection && fg('platform_editor_ai_remix_toolbar')) {
			const $endPos = tr.doc.resolve(endPos);

			tr.setSelection(
				GapCursorSelection.valid($endPos)
					? new GapCursorSelection($endPos, Side.RIGHT)
					: TextSelection.create(tr.doc, endPos),
			);
		} else {
			tr.setSelection(TextSelection.create(tr.doc, endPos));
		}

		return tr;
	};

export function onStartRemixThinking({
	api,
	options,
}: {
	api: ExtractInjectionAPI<MalleableUiPlugin> | undefined;
	options?: {
		contentType?: string;
		dynamicUiSubtypeForUpdate?: string;
		historyEntryForUpdate?: RemixHistoryEntryForUpdate;
		isUpdate?: boolean;
		suppressThinkingHighlight?: boolean;
	};
}): void {
	api?.aiExperience?.actions.toggleAiEntryPoints?.(true);
	api?.core.actions.execute(api?.malleableUi.commands.startRemixThinking(options));
}
